import React from 'react';

// ==================== MUI Material & External Libraries ====================
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    Typography,
    useTheme,
    LinearProgress
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

// ==================== Icons ====================
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';

import axios from 'axios';

// ==================== API Imports ====================
import { useGetTaskNoteModal, useGetTaskNoteFiles, useInsertTaskNote, useUpdateTaskNote, useGenerateUploadFileUrlsForNote, useAfterSuccessfulFileUploadForNote, mutateTaskNoteFiles, useDeleteTaskNoteFile } from 'api/TaskManagement/TaskNote/taskNote';

// ==================== Project Imports ====================
import CompressedFilemanager from 'components/CommonFileManager/CompressedFileManager';
import QuillEditor from 'components/third-party/QuillEditor';
import { FileManagerModule } from 'types/CommonFileManager/commonFileManager';
import { NoteFormProps } from 'types/taskManagement/TaskManagement';
import { GetFormattedDateOnly, GetFormattedTimeOnly } from 'utils/commonFunctions';

// Helper function to handle Note File Deletion via useDeleteTaskNoteFile
const deleteTaskNoteFiles = async (taskNoteID: number, deletedFiles: string[], existingFiles: any[]) => {
    if (!deletedFiles || deletedFiles.length === 0) return;
    for (const fileToDelete of deletedFiles) {
        const cleanToDelete = String(fileToDelete).split(/[\/\\]/).pop()?.toLowerCase() || '';

        const matched = (existingFiles || []).find((f: any) => {
            const fName = f.fileName || f.name || f.filename || f.originalFullPath;
            const cleanFName = fName ? String(fName).split(/[\/\\]/).pop()?.toLowerCase() : '';
            return cleanFName === cleanToDelete;
        });

        const fileID = matched?.fileID || matched?.id || matched?.fileId || matched?.primaryKeyID;
        const cleanFileName = fileToDelete.split(/[\/\\]/).pop() || fileToDelete;

        await useDeleteTaskNoteFile({
            taskNoteID,
            fileID: fileID ? String(fileID) : undefined,
            fileName: cleanFileName
        });
    }
};

// Helper function to handle full Task Note File Upload workflow directly in NoteForm
const uploadTaskNoteFiles = async (taskNoteID: number, files: File[], onProgress?: (fileName: string, percent: number) => void) => {
    if (!files || files.length === 0) return;

    const fileNames = files.map((file) => file.name);

    // Step 1: Generate Upload File URLs for Note
    const res = await useGenerateUploadFileUrlsForNote({ taskNoteID, fileNames });
    const urlList: any[] = Array.isArray(res) ? res : (res?.data || res?.result || []);

    // Step 2: Upload each file to Azure Blob via uploadFileUrl sequentially (one by one)
    for (const file of files) {
        const item = urlList.find((u) => {
            if (!u) return false;
            const uName = String(u.fileName || u.name || u.uploadFileName || '').toLowerCase();
            const fName = file.name.toLowerCase();
            return uName === fName || decodeURIComponent(uName) === decodeURIComponent(fName);
        }) || (urlList.length === 1 ? urlList[0] : null);

        const uploadFileUrl = item?.uploadFileUrl || item?.uploadUrl || item?.url || (urlList.length === 1 ? (urlList[0]?.uploadFileUrl || urlList[0]?.uploadUrl || urlList[0]?.url) : null);

        if (uploadFileUrl) {
            await axios.put(uploadFileUrl, file, {
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'x-ms-blob-type': 'BlockBlob'
                },
                timeout: 0,
                onUploadProgress: (progressEvent) => {
                    if (onProgress) {
                        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
                        onProgress(file.name, percent);
                    }
                }
            });

            // Step 3: Register uploaded note file in DB
            try {
                await useAfterSuccessfulFileUploadForNote({ taskNoteID, fileName: item?.blobFileName || item?.fileName });
            } catch (err) {
                console.error('After successful note file upload call failed:', err);
            }
        }
    }

    // Step 4: Refresh cache for Task Note Files
    mutateTaskNoteFiles(taskNoteID);
};

// Validation Schema using Yup
const NoteSchema = Yup.object().shape({
    note: Yup.string().trim().required('Note is required')
});

export default function NoteForm({ open, closeModal, taskId, selectedNote, onSave }: NoteFormProps) {

    const theme = useTheme();
    const fileManagerRef = React.useRef<any>(null);
    const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
    const [hasFileChanges, setHasFileChanges] = React.useState<boolean>(false);
    const activeNoteIdRef = React.useRef<number | null>(null);
    const isEditNote = Boolean(selectedNote);

    const targetNoteId = isEditNote ? (Number((selectedNote as any)?.taskNoteID || (selectedNote as any)?.primaryKeyID || (selectedNote as any)?.taskNoteId || selectedNote?.id) || null) : null;
    const { data: modalNoteData } = useGetTaskNoteModal(open && isEditNote && targetNoteId ? targetNoteId : null);
    const { data: noteFilesData } = useGetTaskNoteFiles(open && isEditNote && targetNoteId ? targetNoteId : null);

    const initialFiles = React.useMemo(() => {
        if (!isEditNote) return [];
        const rawFiles = noteFilesData && Array.isArray(noteFilesData) && noteFilesData.length > 0
            ? noteFilesData
            : ((selectedNote as any)?.attachments || (selectedNote as any)?.files || (modalNoteData as any)?.files || []);

        return (Array.isArray(rawFiles) ? rawFiles : []).map((f: any) => ({
            id: String(f.fileID || f.id || f.fileId || f.primaryKeyID || ''),
            fileID: f.fileID || f.id || f.fileId || f.primaryKeyID,
            name: f.fileName || f.name || f.filename || 'File',
            size: Number(f.fileSize || f.size || 0),
            notes: f.notes || '',
            modifiedOn: f.modifiedOn || f.createdOn || f.createdDate || '',
            uploadedByUserName: f.uploadedByUserName || f.createdByUserName || ''
        }));
    }, [isEditNote, noteFilesData, selectedNote, modalNoteData]);

    const initialValues = React.useMemo(
        () => ({
            note: modalNoteData?.noteFormatted || modalNoteData?.notePlain || selectedNote?.noteText || ''
        }),
        [modalNoteData, selectedNote?.id, selectedNote?.noteText]
    );

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: NoteSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {

            try {
                if (onSave) {
                    await onSave(values);
                } else {
                    const rawPlain = values.note
                        .replace(/&nbsp;/g, ' ')
                        .replace(/\u00a0/g, ' ')
                        .replace(/<[^>]*>/g, '')
                        .replace(/\s+/g, ' ')
                        .trim();
                    const notePlain = rawPlain ||
                        (/<img[\s>]/i.test(values.note) ? '[image]' :
                            /<table[\s>]/i.test(values.note) ? '[table]' : '');
                    const noteFormatted = values.note;

                    let createdNoteId = isEditNote ? Number(modalNoteData?.taskNoteID || modalNoteData?.primaryKeyID || (selectedNote as any)?.taskNoteID || selectedNote?.id) : 0;

                    if (isEditNote) {
                        const noteId = Number(modalNoteData?.taskNoteID || modalNoteData?.primaryKeyID || (selectedNote as any)?.taskNoteID || selectedNote?.id);
                        const rawUpdatedOn = modalNoteData?.updatedOn || null;
                        const payload = {
                            taskNoteID: noteId,
                            updatedOn: rawUpdatedOn,
                            notePlain,
                            noteFormatted
                        };
                        await useUpdateTaskNote(payload);
                        activeNoteIdRef.current = noteId;
                    } else {
                        const payload = {
                            taskID: Number(taskId),
                            notePlain,
                            noteFormatted
                        };
                        const res = await useInsertTaskNote(payload);
                        createdNoteId = Number(res?.primaryKeyID || res?.taskNoteID || res?.id);
                        activeNoteIdRef.current = createdNoteId;
                    }

                    const targetNoteId = activeNoteIdRef.current || createdNoteId;

                    const localState = fileManagerRef.current?.getLocalState?.();
                    const filesToUpload: File[] = (localState?.added && localState.added.length > 0) ? localState.added : pendingFiles;
                    const deletedFileNames: string[] = localState?.deleted || [];

                    if (fileManagerRef.current?.save && targetNoteId) {
                        await fileManagerRef.current.save(targetNoteId);
                    } else if (targetNoteId) {
                        if (deletedFileNames.length > 0) {
                            await deleteTaskNoteFiles(targetNoteId, deletedFileNames, initialFiles);
                        }
                        if (filesToUpload.length > 0) {
                            await uploadTaskNoteFiles(targetNoteId, filesToUpload);
                        }
                    }

                    if (targetNoteId) {
                        mutateTaskNoteFiles(targetNoteId);
                    }
                }
                resetForm();
                closeModal();
            } catch (err) {
                console.error('Add/Edit note submit error:', err);
            } finally {
                setSubmitting(false);
            }
        }
    });

    const { values, errors, touched, handleSubmit, isSubmitting, setFieldValue, resetForm, dirty } = formik;

    const handleClose = () => {
        if (isSubmitting) return;
        resetForm({ values: { note: '' } });
        closeModal();
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (isSubmitting) return;
                if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    handleClose();
                }
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 1 }
            }}>
            {/* Dialog Header */}
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="span">
                    {isEditNote ? (
                        <>
                            <Typography component="span" sx={{ color: 'primary.main' }}>
                                Edit
                            </Typography>{' '}
                            Note
                        </>
                    ) : (
                        <>
                            <Typography component="span" sx={{ color: 'primary.main' }}>
                                Add
                            </Typography>{' '}
                            Note
                        </>
                    )}
                </Typography>
                <IconButton
                    aria-label="close"
                    disabled={isSubmitting}
                    onClick={handleClose}
                    sx={{
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'primary.lighter' }
                    }}
                >
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <Divider />

            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <DialogContent sx={{ p: 3 }}>
                        <Grid container spacing={2.5}>
                            {/* Rich Text Editor for Note */}
                            <Grid size={{ xs: 12 }}>
                                <Stack spacing={0.75}>
                                    <InputLabel htmlFor="note-editor" required>Note</InputLabel>
                                    <Box sx={{ width: '100%', opacity: isSubmitting ? 0.7 : 1, pointerEvents: isSubmitting ? 'none' : 'auto' }}>
                                        <QuillEditor
                                            value={values.note}
                                            onChange={(content) => setFieldValue('note', content)}
                                            placeholder="Enter Note..."
                                            editorMaxHeight="160px"
                                            editorMinHeight={250}
                                            hideImage={true}
                                            hideTable={true}
                                            hideLink={true}
                                            hideVideo={true}
                                        />
                                    </Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        {touched.note && errors.note ? (
                                            <Typography variant="caption" color="error">
                                                {errors.note as string}
                                            </Typography>
                                        ) : (
                                            <Box />
                                        )}
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* File Attachment Dropzone (CompressedFileManager) */}
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ position: 'relative' }}>
                                    <CompressedFilemanager
                                        ref={fileManagerRef}
                                        saveMode="onSaveClick"
                                        height={300}
                                        saving={isSubmitting}
                                        module={FileManagerModule.TASK_NOTE}
                                        referenceID={targetNoteId || undefined}
                                        initialFiles={initialFiles}
                                        allowUpload={true}
                                        allowDelete={true}
                                        allowRename={false}
                                        allowNotes={false}
                                        allowEditImage={false}
                                        hideDirectoryHeader={true}
                                        hideCheckbox={true}
                                        onChange={(info) => {
                                            setHasFileChanges((info.addedCount || 0) > 0 || (info.deletedCount || 0) > 0 || (info.renamedCount || 0) > 0);
                                        }}
                                        onSave={async (data, onProgress) => {
                                            setPendingFiles(data.uploadedFiles || []);
                                            const targetId = activeNoteIdRef.current || targetNoteId;
                                            if (targetId) {
                                                if (data.deletedFiles && data.deletedFiles.length > 0) {
                                                    await deleteTaskNoteFiles(targetId, data.deletedFiles, initialFiles);
                                                }
                                                if (data.uploadedFiles && data.uploadedFiles.length > 0) {
                                                    await uploadTaskNoteFiles(targetId, data.uploadedFiles, onProgress);
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <Divider />

                    {/* Dialog Footer Actions */}
                    <DialogActions sx={{ p: 2, px: 3 }}>
                        <Grid container justifyContent="space-between" alignItems="center" sx={{ width: 1 }}>
                            <Grid>
                                {((modalNoteData?.createdByUserName || selectedNote?.authorName) || (modalNoteData?.createdOn || selectedNote?.createdDate)) && (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px' }}>
                                        {isEditNote && (modalNoteData?.updatedByUserName || modalNoteData?.updatedOn) ? 'Edited by' : 'Created by'}{' '}
                                        <Typography component="span" sx={{ fontSize: '11px', fontStyle: 'italic', color: 'primary.main' }}>
                                            {modalNoteData?.updatedByUserName || modalNoteData?.createdByUserName || selectedNote?.authorName}
                                        </Typography>{' '}
                                        at{' '}
                                        <Typography component="span" sx={{ fontSize: '11px', fontStyle: 'italic', color: 'primary.main' }}>
                                            {GetFormattedTimeOnly(modalNoteData?.updatedOn || modalNoteData?.createdOn || selectedNote?.createdDate || '')}
                                        </Typography>{' '}
                                        on{' '}
                                        <Typography component="span" sx={{ fontSize: '11px', fontStyle: 'italic', color: 'primary.main' }}>
                                            {GetFormattedDateOnly(modalNoteData?.updatedOn || modalNoteData?.createdOn || selectedNote?.createdDate || '')}
                                        </Typography>
                                    </Typography>
                                )}
                            </Grid>
                            <Grid>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={
                                        isSubmitting ||
                                        (!dirty && !hasFileChanges && isEditNote) ||
                                        !values.note ||
                                        (
                                            values.note.replace(/<[^>]*>/g, '').trim() === '' &&
                                            !/<img[\s>]/i.test(values.note) &&
                                            !/<table[\s>]/i.test(values.note)
                                        )
                                    }
                                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                                    sx={{
                                        borderRadius: 1.5,
                                        px: 2,
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 500
                                    }}
                                >
                                    {isSubmitting ? 'Saving & Uploading...' : (isEditNote ? 'Update Note' : 'Add Note')}
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    );
}
